function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useState } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { IconButton } from 'components/Button/IconButton';
import { CheckCircleIcon, CloseIcon, ExclamationOctagonIcon, InfoIcon } from 'components/Icon';
import * as S from './Snackbar.styles';
const variantIcons = {
  info: InfoIcon,
  error: ExclamationOctagonIcon,
  success: CheckCircleIcon
};
const MININUM_DURATION = 5000;

// Snackbars should appear for at least 5 seconds. This time should be increased by 1 second for every 5 words
export const getDuration = description => {
  const words = description.split(' ').filter(word => word !== '');
  const additionalDuration = Math.floor(words.length / 5) * 1000;
  return MININUM_DURATION + additionalDuration;
};
const BaseSnackbar = /*#__PURE__*/React.memo( /*#__PURE__*/React.forwardRef(({
  description,
  variant = 'success',
  duration: customDuration,
  defaultOpen = true,
  open,
  onOpenChange,
  testId,
  ...rootProps
}, ref) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [startProgress, setStartProgress] = useState(false);
  const VariantIcon = variantIcons[variant];

  // When rapidly toggling `open` from a controlled component in some occasions `open` may be false and `startProgress` true
  // which causes the progress bar to appear in front of the snackbar.
  // This needs to be synced in an effect and not during render so merely comparing `startProgress && open` won't work here.
  useEffect(() => {
    if (!open ?? !internalOpen) {
      setStartProgress(false);
    }
  }, [open, internalOpen]);
  const duration = customDuration ?? getDuration(description);
  // If the progress animation ends exactly on unmount it gives an impression that the snackbar closed before
  // the progress bar reached the end of the element. So we make a little bit faster so that the user can see
  // the bar fully loaded before the component unmounts
  const progressAnimationDuration = duration - 300;
  const handleClose = () => {
    if (onOpenChange) {
      onOpenChange(false);
      return;
    }
    setInternalOpen(false);
  };
  const handleOpenChange = b => {
    if (onOpenChange) {
      onOpenChange(b);
      return;
    }
    setInternalOpen(b);
  };
  const handleSlideInAnimationEnd = () => {
    setStartProgress(true);
  };
  return /*#__PURE__*/React.createElement(S.SnackbarContainer, null, /*#__PURE__*/React.createElement(S.SnackbarRoot, _extends({
    open: open ?? internalOpen,
    onOpenChange: handleOpenChange,
    onAnimationEnd: handleSlideInAnimationEnd,
    duration: duration,
    $variant: variant,
    ref: ref,
    "data-testid": testId
  }, rootProps), /*#__PURE__*/React.createElement(S.SnackbarProgressContainer, null, startProgress && /*#__PURE__*/React.createElement(S.SnackbarProgress, {
    $variant: variant,
    $duration: progressAnimationDuration
  })), /*#__PURE__*/React.createElement(S.VariantIconContainer, null, /*#__PURE__*/React.createElement(VariantIcon, {
    size: "xl",
    variant: "bold"
  })), /*#__PURE__*/React.createElement(Toast.Description, {
    asChild: true
  }, /*#__PURE__*/React.createElement(S.SnackbarDescription, {
    variant: "ui",
    size: "large"
  }, description)), /*#__PURE__*/React.createElement(S.CloseIconContainer, null, /*#__PURE__*/React.createElement(IconButton, {
    onClick: handleClose,
    icon: /*#__PURE__*/React.createElement(CloseIcon, null),
    variant: "text",
    size: "large"
  }))));
}));
const SnackbarWithViewport = /*#__PURE__*/React.forwardRef(({
  label,
  hotkey,
  ...baseProps
}, ref) => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BaseSnackbar, _extends({
    ref: ref
  }, baseProps)), /*#__PURE__*/React.createElement(S.SnackbarViewport, null));
});
const Snackbar = /*#__PURE__*/React.forwardRef(({
  isStacked = false,
  label,
  hotkey,
  ...baseProps
}, ref) => {
  if (isStacked) {
    return /*#__PURE__*/React.createElement(BaseSnackbar, _extends({
      ref: ref
    }, baseProps));
  }
  return /*#__PURE__*/React.createElement(SnackbarWithViewport, _extends({
    label: label,
    hotkey: hotkey,
    ref: ref
  }, baseProps));
});
const SnackbarViewport = /*#__PURE__*/React.forwardRef((props, ref) => {
  return /*#__PURE__*/React.createElement(S.SnackbarViewport, _extends({
    ref: ref
  }, props));
});
export { Snackbar, SnackbarViewport };